/**
 * DownloadButton Component
 *
 * Botão único de download - sem dropdown
 * Baixa o artefato no formato nativo automaticamente
 */

import React, { memo } from 'react';
import { useArtifactDownload } from '../hooks';
import { getNativeFormat } from '../utils';
import type { Artifact } from '../utils';

// =============================================================================
// TYPES
// =============================================================================

interface DownloadButtonProps {
  artifact: Artifact;
  className?: string;
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

// =============================================================================
// ICONS
// =============================================================================

const DownloadIcon: React.FC<{ size: number }> = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" fill="currentColor" />
  </svg>
);

const CheckIcon: React.FC<{ size: number }> = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
      fill="currentColor"
    />
  </svg>
);

const SpinnerIcon: React.FC<{ size: number }> = ({ size }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    className="animate-spin"
  >
    <circle
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeDasharray="32"
      strokeDashoffset="12"
      fill="none"
    />
  </svg>
);

// =============================================================================
// COMPONENT
// =============================================================================

export const DownloadButton: React.FC<DownloadButtonProps> = memo(
  ({ artifact, className = '', showLabel = false, size = 'md' }) => {
    const { isDownloading, success, download } = useArtifactDownload();

    const handleClick = async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isDownloading) return;
      await download(artifact);
    };

    // Tamanhos
    const iconSizes = { sm: 14, md: 16, lg: 20 };
    const iconSize = iconSizes[size];

    // Formato para tooltip
    const format = getNativeFormat(artifact.type).toUpperCase();

    // Classes base
    const baseClasses = [
      'inline-flex items-center justify-center',
      'rounded-md transition-all duration-200',
      'text-gray-400 hover:text-white',
      'hover:bg-white/10',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      'focus:outline-none focus:ring-2 focus:ring-yellow-500/50',
    ];

    // Classes de tamanho
    const sizeClasses = {
      sm: 'p-1 gap-1 text-xs',
      md: 'p-1.5 gap-1.5 text-sm',
      lg: 'p-2 gap-2 text-base',
    };

    // Estado visual
    const stateClasses = success
      ? 'text-green-400 bg-green-500/10'
      : isDownloading
        ? 'text-yellow-400 bg-yellow-500/10'
        : '';

    return (
      <button
        onClick={handleClick}
        disabled={isDownloading}
        className={`${baseClasses.join(' ')} ${sizeClasses[size]} ${stateClasses} ${className}`}
        aria-label={`Baixar como ${format}`}
        title={`Baixar como ${format}`}
      >
        {isDownloading ? (
          <SpinnerIcon size={iconSize} />
        ) : success ? (
          <CheckIcon size={iconSize} />
        ) : (
          <DownloadIcon size={iconSize} />
        )}

        {showLabel && (
          <span className="font-medium">
            {isDownloading ? 'Baixando...' : success ? 'Baixado!' : format}
          </span>
        )}
      </button>
    );
  }
);

DownloadButton.displayName = 'DownloadButton';

export default DownloadButton;
